import React, {
  useContext,
  useReducer,
  useState,
  useRef,
  useCallback,
  forwardRef,
  useEffect,
} from "react";
import {
  FilledInput,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import InputMask from "react-input-mask";
import { NUMERICAL_REGEX } from "@tomocredit/common-fe/consts";
import { STATES } from "utils/constants";
import { MobileContext, States } from "components/common";
import { GOOGLE_ADDRESS_TYPES } from "components/Pages/Boost/consts";
import { VerificationContinueButton } from "./ContinueButton";
import { validatePersonalInfo } from "./helpers";
// eslint-disable-next-line no-unused-vars
import * as Types from "../CreditMonitoring.types";

const formatSSN = (ssn = "") => {
  const cleaned = ssn.replace(NUMERICAL_REGEX, "");
  let formattedSSN = "";

  // allow user to enter "-" manually.
  if (
    (ssn.length === 4 && ssn[3] === "-") ||
    (ssn.length === 7 && ssn[6] === "-")
  ) {
    return ssn;
  }

  // Check if the ssn contains only digits
  if (/^\d+$/.test(cleaned)) {
    formattedSSN = cleaned;

    // Add hyphen for the ssn format XXX-XX-XXXX
    if (cleaned.length > 3) {
      formattedSSN = `${cleaned.slice(0, 3)}-`;

      if (cleaned.length > 5) {
        formattedSSN += `${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
      } else {
        formattedSSN += `${cleaned.slice(3)}`;
      }
    }

    return formattedSSN;
  }

  return formattedSSN;
};

/**
 * @param {Object} props
 * @param {String} props.htmlFor
 * @param {Boolean} props.isRequired - default is true because most of the fields are required
 * @returns
 */
const Label = ({ children, htmlFor, isRequired = true }) => {
  return (
    <InputLabel
      className="font-bold mb-1 text-black"
      style={{ lineHeight: "150%", color: "black" }}
      htmlFor={htmlFor}
      required={isRequired}
    >
      {children}
    </InputLabel>
  );
};

const commonInputProps = { style: { padding: 12 } };

const InputWithLabel = forwardRef(
  ({ isDisabled, isRequired, inputProps, ...otherProps }, inputRef) => {
    return (
      <>
        <Label isRequired={isRequired} htmlFor={otherProps?.id}>
          {otherProps?.label}
        </Label>
        {isDisabled ? (
          <FilledInput
            inputRef={inputRef}
            className="w-full"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...otherProps}
            disabled={isDisabled}
            inputProps={{ ...commonInputProps, ...inputProps }}
          />
        ) : (
          <OutlinedInput
            inputRef={inputRef}
            className="w-full"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...otherProps}
            notched={false}
            inputProps={{ ...commonInputProps, ...inputProps }}
          />
        )}
      </>
    );
  },
);

const phoneNumberMask = "(999) 999-9999";

/**
 * @param {Object} props
 * @param {Types.UserInfo} props.userInfo
 * @param {Boolean} props.userLoading
 * @param {Array<String>} props.disabledFields
 * @param {Function} props.onContinue
 */
export const VerificationPersonalInfo = ({
  userInfo,
  userLoading,
  disabledFields,
  onContinue,
  onChangeField,
}) => {
  const { isMobile } = useContext(MobileContext);

  const { firstName, lastName, dateOfBirth, ssn, phoneNumber } = userInfo;
  const [isSsnVisible, toggleSsnVisibility] = useReducer(
    (visible) => !visible,
    false,
  );
  const [error, setError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const autocomplete = useRef(null);

  useEffect(() => {
    window.dataLayer.push({
      event: "CMD_PERSONAL_INFO",
    });
  }, []);

  const autocompleteRef = useCallback((node) => {
    if (node !== null) {
      const fillInAddress = () => {
        // Get the place details from the autocomplete object.
        const place = autocomplete.current?.getPlace();
        let acAddress1 = "";
        let acPostcode = "";
        let acState = "";
        let acLocality = "";

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.
        // place.address_components are google.maps.GeocoderAddressComponent objects
        place.address_components.forEach((component) => {
          const componentType = component.types[0];
          switch (componentType) {
            case GOOGLE_ADDRESS_TYPES.STREET_NUMBER: {
              acAddress1 = `${component.long_name} ${userInfo.address.street}`;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.ROUTE: {
              acAddress1 += component.short_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.POSTAL_CODE: {
              acPostcode = component.long_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.LOCALITY: {
              acLocality = component.long_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.ADMINISTRATIVE_AREA_LEVEL_1: {
              acState = component.short_name;
              break;
            }
            default:
              break;
          }
        });
        onChangeField("address", {
          street: acAddress1,
          unit: "",
          city: acLocality,
          state: acState,
          zip: acPostcode,
        });
      };
      const initAutocomplete = () => {
        autocomplete.current = new window.google.maps.places.Autocomplete(
          node,
          {
            componentRestrictions: { country: ["us"] },
            fields: ["address_components"],
            types: ["address"],
          },
        );
        // When the user selects an address from the drop-down, populate the
        // address fields in the form.
        autocomplete.current.addListener("place_changed", fillInAddress);
      };

      initAutocomplete();
    }
  }, []);

  const submitUserInfo = async () => {
    setSubmitLoading(true);
    setError("");
    window.dataLayer.push({
      event: "CMD_PERSONAL_INFO_SUBMITTED",
    });
    try {
      const { error: personalInfoError, isValid } =
        validatePersonalInfo(userInfo);
      if (isValid) {
        onContinue();
      } else {
        setError(personalInfoError);
      }
    } catch (err) {
      // ignore
      const errorMessage =
        err?.graphQLErrors?.[0]?.message ||
        err?.graphQLErrors?.[0]?.displayMessage ||
        "An error occurred";
      setError(errorMessage);
      window.dataLayer.push({
        event: "CMD_PERSONAL_INFO_ERROR",
        error: errorMessage,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full relative">
      <div className="">
        <p className="text-2xl font-bold" style={{ lineHeight: "120%" }}>
          Let&apos;s review your information
        </p>
        {userLoading ? (
          <div className="loader" style={{ width: 40, height: 40 }} />
        ) : (
          <div className="grid grid-cols-1 gap-6 mt-6 xl:grid-cols-10 w-full">
            <div className="col-span-10 xl:col-span-5">
              <InputWithLabel
                label="First Name"
                id="first-name"
                placeholder="First Name"
                isDisabled={disabledFields.includes("firstName")}
                value={`${firstName}`.toUpperCase()}
                onChange={(e) => onChangeField("firstName", e.target.value)}
              />
            </div>
            <div className="col-span-10 xl:col-span-5">
              <InputWithLabel
                label="Last Name"
                id="last-name"
                placeholder="Last Name"
                isDisabled={disabledFields.includes("lastName")}
                value={`${lastName}`.toUpperCase()}
                onChange={(e) => onChangeField("lastName", e.target.value)}
              />
            </div>
            <div className="col-span-10 xl:col-span-5">
              <Label htmlFor="dob">Date of Birth</Label>
              <DatePicker
                id="dob"
                format="MM/DD/YYYY"
                className="w-full"
                inputVariant={
                  disabledFields.includes("dateOfBirth") ? "filled" : "outlined"
                }
                disabled={disabledFields.includes("dateOfBirth")}
                value={dateOfBirth}
                onChange={(date) =>
                  onChangeField(
                    "dateOfBirth",
                    moment(date).format("MM/DD/YYYY"),
                  )
                }
                inputProps={commonInputProps}
              />
            </div>
            <div className="col-span-10 xl:col-span-5">
              <Label htmlFor="phone">Phone number</Label>
              <InputMask
                mask={phoneNumberMask}
                value={phoneNumber}
                placeholder="(___) ___-____"
                onChange={(e) =>
                  onChangeField(
                    "phoneNumber",
                    e.target.value.replace(/\D/g, ""),
                  )
                }
                disabled={disabledFields.includes("phoneNumber")}
                inputProps={commonInputProps}
              >
                <TextField
                  id="phone"
                  label=""
                  variant={
                    disabledFields.includes("phoneNumber")
                      ? "filled"
                      : "outlined"
                  }
                  className="w-full"
                />
              </InputMask>
            </div>
            <div className="col-span-10">
              <InputWithLabel
                label="Social security number"
                id="ssn"
                placeholder="Full social security number"
                isDisabled={disabledFields.includes("ssn")}
                value={disabledFields.includes("ssn") ? "***-**-***" : ssn}
                inputProps={{ maxLength: 11 }}
                onChange={(e) =>
                  onChangeField("ssn", formatSSN(e.target.value))
                }
                type={(isSsnVisible || disabledFields.includes("ssn")) ? "text" : "password"}
                endAdornment={
                  disabledFields.includes("ssn") ? null :
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleSsnVisibility}
                        edge="end"
                        style={{ padding: 8, outline: "none" }}
                      >
                        {isSsnVisible ? (
                          <i className="fa-regular fa-eye" />
                        ) : (
                          <i className="fa-regular fa-eye-slash" />
                        )}
                      </IconButton>
                    </InputAdornment>
                }
              />
            </div>
            <div className="col-span-10 xl:col-span-8">
              <InputWithLabel
                ref={disabledFields.includes("street") ? null : autocompleteRef}
                label="Street"
                id="street"
                placeholder="Street address"
                isDisabled={disabledFields.includes("street")}
                value={userInfo.address.street}
                onChange={(e) =>
                  onChangeField("address", {
                    ...userInfo.address,
                    street: e.target.value,
                  })
                }
              />
            </div>
            <div className="col-span-10 xl:col-span-2">
              <InputWithLabel
                label="Apt"
                id="unit"
                placeholder="Apt"
                isRequired={false}
                isDisabled={disabledFields.includes("unit")}
                value={userInfo.address.unit}
                onChange={(e) =>
                  onChangeField("address", {
                    ...userInfo.address,
                    unit: e.target.value,
                  })
                }
              />
            </div>
            <div className="col-span-10 xl:col-span-4">
              <InputWithLabel
                label="City"
                id="city"
                placeholder="City"
                isDisabled={disabledFields.includes("city")}
                value={userInfo.address.city}
                onChange={(e) =>
                  onChangeField("address", {
                    ...userInfo.address,
                    city: e.target.value,
                  })
                }
              />
            </div>
            <div className="col-span-10 xl:col-span-4">
              {disabledFields.includes("state") ? (
                <InputWithLabel
                  label="State"
                  id="state"
                  isDisabled
                  value={`${
                    STATES.find((s) => s.value === userInfo.address.state)
                      ?.label || userInfo.address.state
                  }`.toUpperCase()}
                />
              ) : (
                <>
                  <Label>State</Label>
                  <States
                    className="w-full font-medium rounded p-2 border border-[#C0C0C0] hover:border-black bg-neutral-50 customized-select"
                    style={{ height: 43, minWidth: 120 }}
                    value={userInfo.address.state}
                    onSelect={(state) =>
                      onChangeField("address", {
                        ...userInfo.address,
                        state: state.value,
                      })}
                  />
                </>
              )}
            </div>
            <div className="col-span-10 xl:col-span-2" style={{ minWidth: 70 }}>
              <InputWithLabel
                label="Zip"
                id="zip"
                placeholder="Zip"
                inputProps={{ maxLength: 5 }}
                isDisabled={disabledFields.includes("zip")}
                value={userInfo.address.zip}
                onChange={(e) =>
                  onChangeField("address", {
                    ...userInfo.address,
                    zip: e.target.value,
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
      <div
        className={
          isMobile ? "sticky bottom-0 left-0 right-0 bg-white py-2" : ""
        }
      >
        <VerificationContinueButton
          onClick={submitUserInfo}
          error={error}
          loading={submitLoading}
        />
      </div>
    </div>
  );
};
